import { Linkedin, MessageSquare } from 'lucide-react';

import { PortalEmptyState } from '@/modules/hiring-manager/components/portalEmptyState/PortalEmptyState';
import type { UserExternalAccountApi } from '@/modules/user-external-accounts/types';

interface MessagesEmptyProps {
  /** The user's LinkedIn account, if they have one at all. */
  linkedInStatus: UserExternalAccountApi['status'] | undefined;
  /** Undefined when this workspace cannot authorize LinkedIn. */
  integrationsHref?: string;
  hasFilters: boolean;
  onClearFilters: () => void;
  hasNoLiveJobs: boolean;
  addJobHref: string;
  draftJobHref?: string;
}

/**
 * Why the log is empty, in the order the user can act on it: a filter they set,
 * an outreach account that cannot send, a draft or no live job, or Finlay
 * simply not having reached anyone yet.
 */
export function MessagesEmpty({
  linkedInStatus,
  integrationsHref,
  hasFilters,
  onClearFilters,
  hasNoLiveJobs,
  addJobHref,
  draftJobHref,
}: MessagesEmptyProps) {
  if (hasFilters) {
    return (
      <PortalEmptyState
        action={{ label: 'Clear filters', onClick: onClearFilters }}
        description="Try a wider date range or another job."
        icon={MessageSquare}
        size="row"
        title="No matching messages"
      />
    );
  }

  // The account exists but cannot send, so nothing will arrive here until it is
  // reconnected. Saying "nothing sent yet" would blame the wrong thing.
  if (linkedInStatus === 'Disconnected' || linkedInStatus === 'Failed') {
    return (
      <PortalEmptyState
        {...(integrationsHref
          ? { action: { label: 'Reconnect LinkedIn', to: integrationsHref } }
          : {})}
        description={
          integrationsHref
            ? 'Reconnect your account so Finlay can send messages again.'
            : 'LinkedIn is disconnected. This workspace cannot reconnect it. Ask your recruiter for help.'
        }
        icon={Linkedin}
        title="LinkedIn disconnected"
      />
    );
  }

  if (linkedInStatus === 'Pending') {
    return (
      <PortalEmptyState
        description="Finlay can start sending messages once your account is connected."
        icon={Linkedin}
        live
        title="Connecting LinkedIn"
      />
    );
  }

  if (draftJobHref) {
    return (
      <PortalEmptyState
        action={{ label: 'Finish draft', to: draftJobHref }}
        description="Finish your draft and start sourcing before Finlay can reach out."
        icon={MessageSquare}
        title="Nothing sent yet"
      />
    );
  }

  if (hasNoLiveJobs) {
    return (
      <PortalEmptyState
        action={{ label: 'Create a job', to: addJobHref }}
        description="Create a job and start sourcing to begin outreach on LinkedIn."
        icon={MessageSquare}
        title="Nothing sent yet"
      />
    );
  }

  return (
    <PortalEmptyState
      description="Finlay will message candidates as it finds them. Sent messages and replies will appear here."
      icon={MessageSquare}
      live
      title="No messages sent yet"
    />
  );
}
